import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NodeDetail from './NodeDetail';
import ControlsPanel from './ControlsPanel';
import AdminPanel from './AdminPanel';
import GraphErrorBoundary from './GraphErrorBoundary';
import { curationApi, graphApi, typeApi, proposalApi } from '../services/api';
import { useAuthContext } from '../contexts/AuthContext';
import { useSession } from '../contexts/SessionContext';
import ForceDirectedView from './ForceDirectedView';
import ForceGraph3DView from './ForceGraph3DView';
import IcicleView from './IcicleView';
import ContentPanel from './ContentPanel';
import type { ContentPanelHandle } from './ContentPanel';
import InfoOverlay from './InfoOverlay';
import LocalDemoSwitcher from './LocalDemoSwitcher';
import LinkContextMenu from './LinkContextMenu';
import TypeFilterLegend from './TypeFilterLegend';
import type { LinkMenuInfo } from './LinkContextMenu';
import { trackEvent, trackPageLoad } from '../services/telemetry';
import {
  expandTypeTargets,
  getPairingSourceId,
  getPairingTargetId,
  relationshipToPairings,
} from '../utils/relationshipRules';
import type {
  BatchProposalItem,
  NodeInstance,
  Relationship,
  NodeType,
  RelationshipType,
  Graph,
  HierarchyNode,
  RelCreationState,
  ReparentArgs,
  ProposalSubmitEntry,
  SearchResult,
} from '../types';
import './Layout.css';

// Helper: recursively delete a node by ID from a hierarchy tree (mutates in place)
function deleteNodeFromHierarchy(node: HierarchyNode, id: string): boolean {
  if (!node || !Array.isArray(node.children)) return false;
  const idx = node.children.findIndex((child) => child.id === id);
  if (idx !== -1) {
    node.children.splice(idx, 1);
    return true;
  }
  for (const child of node.children) {
    if (deleteNodeFromHierarchy(child, id)) return true;
  }
  return false;
}

// Helper: recursively search for a node by ID in a hierarchy tree
function findNodeInHierarchy(node: HierarchyNode | null, id: string): HierarchyNode | null {
  if (!node) return null;
  if (node.id === id) return node;
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findNodeInHierarchy(child, id);
      if (found) return found;
    }
  }
  return null;
}

/** Recursively insert newNode as a child of the node matching parentId (mutates in place). */
function addNodeToHierarchy(
  root: HierarchyNode,
  parentId: string,
  newNode: HierarchyNode
): boolean {
  if (root.id === parentId) {
    root.children = [...(root.children || []), newNode];
    return true;
  }
  for (const child of root.children || []) {
    if (addNodeToHierarchy(child, parentId, newNode)) return true;
  }
  return false;
}

/** Recursively update a scalar field on the node matching nodeId (mutates in place). */
function updateNodeInHierarchy(
  root: HierarchyNode,
  nodeId: string,
  field: string,
  value: string
): boolean {
  if (root.id === nodeId) {
    (root as Record<string, unknown>)[field] = value;
    return true;
  }
  for (const child of root.children || []) {
    if (updateNodeInHierarchy(child, nodeId, field, value)) return true;
  }
  return false;
}

function buildParticularMetadata(
  properties: Record<string, unknown> | undefined,
  force = false
): NodeInstance['particular'] {
  const externalId = String(properties?.external_id ?? '').trim();
  const location = String(properties?.location ?? '').trim();
  if (!force && externalId === '' && location === '') {
    return undefined;
  }
  const missingFields: string[] = [];
  if (externalId === '') {
    missingFields.push('external_id');
  }
  if (location === '') {
    missingFields.push('location');
  }
  return {
    external_id: externalId || undefined,
    location: location || undefined,
    missing_fields: missingFields,
  };
}

function normalizeAliasesValue(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const aliases = value
      .filter((alias): alias is string => typeof alias === 'string')
      .map((alias) => alias.trim())
      .filter((alias) => alias.length > 0);
    return aliases.length > 0 ? aliases : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return undefined;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeAliasesValue(parsed);
      }
    } catch {
      const aliases = trimmed
        .split(',')
        .map((alias) => alias.trim())
        .filter((alias) => alias.length > 0);
      return aliases.length > 0 ? aliases : undefined;
    }
  }

  return undefined;
}

function applyNodeFieldUpdate(
  node: NodeInstance,
  fieldName: string,
  fieldValue: unknown
): NodeInstance {
  const textValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '');
  if (fieldName === 'name' || fieldName === 'description') {
    return { ...node, [fieldName]: textValue };
  }
  const properties = {
    ...(node.properties ?? {}),
    [fieldName]: fieldValue,
  };
  const shouldExposeParticular =
    Boolean(node.particular) ||
    Object.prototype.hasOwnProperty.call(properties, 'external_id') ||
    Object.prototype.hasOwnProperty.call(properties, 'location');

  const aliases =
    fieldName === 'aliases'
      ? normalizeAliasesValue(fieldValue)
      : (normalizeAliasesValue(properties.aliases) ?? node.aliases);

  return {
    ...node,
    aliases,
    properties,
    particular: buildParticularMetadata(properties, shouldExposeParticular),
  };
}

function nodePreviewDiffers(
  baseNode: NodeInstance | undefined,
  previewNode: NodeInstance
): boolean {
  if (!baseNode) return true;
  if (baseNode.name !== previewNode.name || baseNode.description !== previewNode.description) {
    return true;
  }
  return JSON.stringify(baseNode.properties ?? {}) !== JSON.stringify(previewNode.properties ?? {});
}

function getNeighbors(graph: Graph, nodeId: string): string[] {
  const neighbors = new Set<string>();
  for (const rel of graph.relationships) {
    if (rel.from_node_id === nodeId) neighbors.add(rel.to_node_id);
    if (rel.to_node_id === nodeId) neighbors.add(rel.from_node_id);
  }
  return Array.from(neighbors);
}

function neighborhoodSet(graph: Graph, centerNodeId: string, hops: number): Set<string> {
  const maxDepth = Math.max(1, hops);
  const visited = new Set<string>([centerNodeId]);
  let frontier: string[] = [centerNodeId];

  for (let depth = 0; depth < maxDepth; depth += 1) {
    const next: string[] = [];
    for (const current of frontier) {
      for (const neighbor of getNeighbors(graph, current)) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.push(neighbor);
        }
      }
    }
    frontier = next;
    if (frontier.length === 0) break;
  }

  return visited;
}

function applyGraphFilters(
  graph: Graph | null,
  selectedNodeTypes: string[],
  hiddenNodeIds: Set<string>,
  ghostNodeIds: Set<string>,
  alwaysIncludeNodeIds: Set<string>,
  edgeDepthCenter: string | null,
  edgeDepth: number
): Graph | null {
  if (!graph) return null;

  const filteredIds = new Set(
    graph.nodes
      .filter((n) => selectedNodeTypes.length === 0 || selectedNodeTypes.includes(n.type))
      .map((n) => n.id)
  );

  // Remove fully-hidden nodes but keep ghost nodes.
  for (const hiddenId of hiddenNodeIds) {
    if (!ghostNodeIds.has(hiddenId)) {
      filteredIds.delete(hiddenId);
    }
  }

  for (const pinnedId of alwaysIncludeNodeIds) {
    filteredIds.add(pinnedId);
  }

  // Always apply edge-depth filtering from the center node.
  if (edgeDepthCenter && filteredIds.has(edgeDepthCenter)) {
    const depthIds = neighborhoodSet(graph, edgeDepthCenter, edgeDepth);
    for (const nodeId of Array.from(filteredIds)) {
      if (!depthIds.has(nodeId) && !alwaysIncludeNodeIds.has(nodeId)) {
        filteredIds.delete(nodeId);
      }
    }
  }

  const nodes = graph.nodes.filter((n) => filteredIds.has(n.id));
  const relationships = graph.relationships.filter(
    (r) => filteredIds.has(r.from_node_id) && filteredIds.has(r.to_node_id)
  );

  return { nodes, relationships };
}

function applyNodeTypeColors(graph: Graph | null, nodeTypes: NodeType[]): Graph | null {
  if (!graph) return null;

  const typeToColor = new Map<string, string>(
    nodeTypes.map((nt) => [nt.type.toLowerCase(), nt.color])
  );

  return {
    ...graph,
    nodes: graph.nodes.map((n) => ({
      ...n,
      color:
        n.color && n.color.trim() !== ''
          ? n.color
          : (typeToColor.get((n.type ?? '').toLowerCase()) ?? n.color),
    })),
  };
}

/**
 * Apply staged queue entries (0..upToIndex) on top of `base` (the pre-session snapshot)
 * to generate a shadow preview graph.
 */
function applyQueueEntriesToGraph(
  base: Graph,
  queue: readonly ProposalSubmitEntry[],
  upToIndex: number
): Graph {
  let nodes = [...base.nodes];
  let relationships = [...base.relationships];
  for (let i = 0; i <= upToIndex && i < queue.length; i++) {
    const entry = queue[i];
    if (entry.entity_type === 'node') {
      if (entry.action === 'add') {
        const aliases = Array.isArray(entry.node_properties?.aliases)
          ? entry.node_properties.aliases.filter(
              (alias): alias is string => typeof alias === 'string'
            )
          : undefined;
        const newNode: NodeInstance = {
          id: entry.node_id ?? '',
          type: entry.node_type ?? '',
          name: entry.node_name ?? '',
          description: entry.node_description ?? '',
          aliases,
          properties: entry.node_properties,
          particular: buildParticularMetadata(
            entry.node_properties,
            Boolean(
              entry.node_properties &&
              (Object.prototype.hasOwnProperty.call(entry.node_properties, 'external_id') ||
                Object.prototype.hasOwnProperty.call(entry.node_properties, 'location'))
            )
          ),
        };
        if (!nodes.some((n) => n.id === newNode.id)) nodes = [...nodes, newNode];
      } else if (entry.action === 'delete') {
        nodes = nodes.filter((n) => n.id !== entry.node_id);
        relationships = relationships.filter(
          (r) => r.from_node_id !== entry.node_id && r.to_node_id !== entry.node_id
        );
      } else if (entry.action === 'update' && entry.field_name) {
        nodes = nodes.map((n) =>
          n.id === entry.node_id ? applyNodeFieldUpdate(n, entry.field_name!, entry.field_value) : n
        );
      }
    } else {
      const newRel: Relationship = {
        id: `preview-${entry.from_node_id}-${entry.to_node_id}`,
        type: entry.rel_type ?? '',
        from_node_id: entry.from_node_id ?? '',
        to_node_id: entry.to_node_id ?? '',
      };
      if (entry.action === 'add') {
        relationships = [...relationships, newRel];
      } else if (entry.action === 'delete') {
        relationships = relationships.filter(
          (r) => !(r.from_node_id === entry.from_node_id && r.to_node_id === entry.to_node_id)
        );
      }
    }
  }
  return { nodes, relationships };
}

function extractAliases(properties?: Record<string, unknown>): string[] | undefined {
  if (!properties) {
    return undefined;
  }
  const aliasesValue = properties.aliases;
  if (Array.isArray(aliasesValue)) {
    const aliases = aliasesValue.filter(
      (alias): alias is string => typeof alias === 'string' && alias.trim() !== ''
    );
    return aliases.length > 0 ? aliases : undefined;
  }
  if (typeof aliasesValue === 'string' && aliasesValue.trim() !== '') {
    try {
      const parsed = JSON.parse(aliasesValue);
      if (Array.isArray(parsed)) {
        const aliases = parsed.filter(
          (alias): alias is string => typeof alias === 'string' && alias.trim() !== ''
        );
        return aliases.length > 0 ? aliases : undefined;
      }
    } catch {
      return aliasesValue
        .split(',')
        .map((alias) => alias.trim())
        .filter((alias): alias is string => alias.length > 0);
    }
  }
  return undefined;
}

// React StrictMode re-mounts components in development; keep one page-load event per true page load.
let pageLoadTracked = false;

function getInitialViewState(): {
  layout: 'force' | 'icicle' | '3d' | 'content';
  schemaMode: boolean;
  contentSourceTarget: { indexName: string; documentId: string; chunk: string | null } | null;
  focusNodeId: string | null;
} {
  const params = new URLSearchParams(window.location.search);
  const rawView = params.get('view');
  const view = rawView === 'schema' ? '3d' : rawView;
  const layout =
    view && ['force', 'icicle', '3d', 'content'].includes(view)
      ? (view as 'force' | 'icicle' | '3d' | 'content')
      : '3d';
  const indexName = params.get('index');
  const documentId = params.get('doc');
  const chunk = params.get('chunk');

  return {
    layout,
    schemaMode: rawView === 'schema',
    contentSourceTarget: indexName ? { indexName, documentId: documentId ?? '', chunk } : null,
    focusNodeId: params.get('focus'),
  };
}

const BFO_CLASS_DEFINITIONS = [
  {
    id: 'bfo-entity',
    name: 'Entity',
    description: 'Anything that exists.',
    parentId: null,
  },
  {
    id: 'bfo-continuant',
    name: 'Continuant',
    description: 'Persists through time while maintaining identity.',
    parentId: 'bfo-entity',
  },
  {
    id: 'bfo-occurrent',
    name: 'Occurrent',
    description: 'Happens or unfolds over time.',
    parentId: 'bfo-entity',
  },
  {
    id: 'bfo-independent-continuant',
    name: 'Independent Continuant',
    description: 'Exists on its own, not dependent on another bearer.',
    parentId: 'bfo-continuant',
  },
  {
    id: 'bfo-specifically-dependent-continuant',
    name: 'Specifically Dependent Continuant',
    description: 'Depends on a specific bearer to exist.',
    parentId: 'bfo-continuant',
  },
  {
    id: 'bfo-generically-dependent-continuant',
    name: 'Generically Dependent Continuant',
    description: 'Information-like content that can have multiple bearers.',
    parentId: 'bfo-continuant',
  },
  {
    id: 'bfo-process',
    name: 'Process',
    description: 'A temporally extended occurrent.',
    parentId: 'bfo-occurrent',
  },
  {
    id: 'bfo-role',
    name: 'Role',
    description: 'A specifically dependent continuant realized in contexts and processes.',
    parentId: 'bfo-specifically-dependent-continuant',
  },
] as const;

const BFO_CLASS_ID_BY_NAME: Record<string, string> = {
  Entity: 'bfo-entity',
  Continuant: 'bfo-continuant',
  Occurrent: 'bfo-occurrent',
  'Independent Continuant': 'bfo-independent-continuant',
  'Specifically Dependent Continuant': 'bfo-specifically-dependent-continuant',
  'Generically Dependent Continuant': 'bfo-generically-dependent-continuant',
  Process: 'bfo-process',
  Role: 'bfo-role',
};

const BFO_CLASS_DETAILS_BY_ID: Record<
  string,
  { name: string; description: string; parentId: string | null }
> = Object.fromEntries(
  BFO_CLASS_DEFINITIONS.map((bfoClass) => [
    bfoClass.id,
    {
      name: bfoClass.name,
      description: bfoClass.description,
      parentId: bfoClass.parentId,
    },
  ])
);

/**
 * Layout component - Main application layout with navigation and content areas
 * Manages the overall UI structure and state for the application
 */
const Layout: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading, permissions, hasPermission, sub, email, login, logout } = useAuthContext();
  const { proposeBatch, startSession, resumeSession, saveSession, cancelSession, sessionActive, currentSessionId, queue } = useSession();
  const initialViewState = useMemo(() => getInitialViewState(), []);

  // Recovery: if auth loading is stuck for >5s, offer sign-in/retry instead of white screen.
  const [authStuck, setAuthStuck] = useState(false);
  useEffect(() => {
    if (!isAuthLoading) {
      setAuthStuck(false);
      return;
    }
    const timer = setTimeout(() => setAuthStuck(true), 5000);
    return () => clearTimeout(timer);
  }, [isAuthLoading]);

  const canUseContent = hasPermission('proposer');
  const canEdit = hasPermission('proposer') || hasPermission('curator');
  const isAdmin = hasPermission('admin');
  /** Mutations are only permitted inside an active edit session. */
  const canMutate = canEdit && sessionActive;
  /**
   * Snapshot of graphData captured when an edit session starts.
   * Used as the baseline for local-queue preview (before any staged edits are applied).
   */
  const preSessionGraphRef = useRef<Graph | null>(null);
  /** When non-null, shown in place of graphData in the active graph view (preview mode). */
  const [sessionPreviewData, setSessionPreviewData] = useState<Graph | null>(null);
  const [nodeTypes, setNodeTypes] = useState<NodeType[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [layout, setLayout] = useState<'force' | 'icicle' | '3d' | 'content'>(
    initialViewState.layout
  );
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const raw = Number.parseInt(localStorage.getItem('dewey.sidebarWidth') || '350', 10);
    if (Number.isNaN(raw)) return 350;
    return Math.max(250, Math.min(650, raw));
  });
  const [sessionRefreshKey, setSessionRefreshKey] = useState<number>(0);
  const [layoutKey, setLayoutKey] = useState<number>(0);
  const [contentSourceTarget, setContentSourceTarget] = useState<{
    indexName: string;
    documentId: string;
    chunk: string | null;
  } | null>(initialViewState.contentSourceTarget);
  const handleContentTargetChange = useCallback(
    (target: { indexName: string; documentId: string; chunk: string | null } | null) => {
      setContentSourceTarget((prev) => {
        if (target === null) {
          return prev?.indexName
            ? { indexName: prev.indexName, documentId: '', chunk: null }
            : null;
        }
        if (
          prev?.indexName === target.indexName &&
          prev?.documentId === target.documentId &&
          prev?.chunk === target.chunk
        ) {
          return prev;
        }
        return target;
      });
    },
    []
  );
  const appContentRef = useRef<HTMLDivElement | null>(null);
  const contentPanelRef = useRef<ContentPanelHandle | null>(null);
  const [reviewSelectedSession, setReviewSelectedSession] = useState<string | null>(null);
  const [schemaMode, setSchemaMode] = useState<boolean>(initialViewState.schemaMode);
  const [selectedNodeTypes, setSelectedNodeTypes] = useState<string[]>(() => {
    try {
      const raw = sessionStorage.getItem('dewey.nodeTypeFilter');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [hiddenNodeIds, setHiddenNodeIds] = useState<Set<string>>(new Set());
  const [ghostNodeIds, setGhostNodeIds] = useState<Set<string>>(new Set());
  const [edgeDepth] = useState<number>(() => {
    const raw = Number.parseInt(sessionStorage.getItem('dewey.edgeDepth') || '12', 10);
    return Number.isNaN(raw) ? 12 : Math.min(12, Math.max(1, raw));
  });
  const [linkMenuState, setLinkMenuState] = useState<{
    position: { x: number; y: number };
    link: LinkMenuInfo;
  } | null>(null);
  const [relCreation, setRelCreation] = useState<RelCreationState>({
    active: false,
    sourceNode: null,
    relType: null,
    relationshipName: null,
    relationshipDirection: null,
    allowedTargets: [],
    error: null,
  });
  const relCreationRef = useRef(relCreation);
  const sessionActiveRef = useRef(sessionActive);
  const lastGraphNodeClickAtRef = useRef<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const hasLoadedGraphOnceRef = useRef(false);
  const [graphData, setGraphData] = useState<Graph | null>(null);
  const schemaGraphData = useMemo<Graph>(() => {
    const nodes: NodeInstance[] = nodeTypes.map((nt) => ({
      id: nt.type,
      type: 'SchemaType',
      name: nt.name || nt.type,
      description: nt.bfo_class
        ? `${nt.description || ''}${nt.description ? '\n\n' : ''}BFO Class: ${nt.bfo_class}${nt.bfo_explanation ? `\n${nt.bfo_explanation}` : ''}`
        : nt.description || '',
      color: nt.color,
    }));

    const relationships: Relationship[] = [];
    let idx = 0;
    for (const relType of relationshipTypes) {
      for (const pairing of relationshipToPairings(relType)) {
        const sourcePattern = getPairingSourceId(pairing);
        const targetPattern = getPairingTargetId(pairing);
        const allNodeTypeIds = nodeTypes.map((n) => n.type);
        const sources = expandTypeTargets(sourcePattern, allNodeTypeIds);
        const targets = expandTypeTargets(targetPattern, allNodeTypeIds);
        for (const src of sources) {
          for (const dst of targets) {
            relationships.push({
              id: `schema-${idx++}`,
              type: relType.name,
              from_node_id: src,
              to_node_id: dst,
            });
          }
        }
      }
    }

    return { nodes, relationships };
  }, [nodeTypes, relationshipTypes]);

  const schemaBfoAssignments = useMemo<Record<
    string,
    { id: string; name: string; description: string; parentId: string | null }
  > | null>(() => {
    if (!schemaMode) {
      return null;
    }

    const assignments: Record<
      string,
      { id: string; name: string; description: string; parentId: string | null }
    > = {};
    for (const nodeType of nodeTypes) {
      const bfoClassId = BFO_CLASS_ID_BY_NAME[nodeType.bfo_class ?? ''];
      if (!bfoClassId) {
        continue;
      }
      const details = BFO_CLASS_DETAILS_BY_ID[bfoClassId];
      if (!details) {
        continue;
      }
      assignments[nodeType.type] = {
        id: bfoClassId,
        name: details.name,
        description: details.description,
        parentId: details.parentId,
      };
    }
    return assignments;
  }, [nodeTypes, schemaMode]);

  const activeGraphData = useMemo(
    () => applyNodeTypeColors(sessionPreviewData ?? graphData, nodeTypes),
    [sessionPreviewData, graphData, nodeTypes]
  );

  // Derived: node IDs added by the preview that don't exist in the base graph.
  // These must survive edge-depth filtering so preview changes are visible.
  const previewPinnedNodeIds = useMemo<Set<string>>(() => {
    if (!sessionPreviewData || !graphData) return new Set();
    const baseNodes = new Map(graphData.nodes.map((n) => [n.id, n]));
    return new Set(
      sessionPreviewData.nodes
        .filter((n) => nodePreviewDiffers(baseNodes.get(n.id), n))
        .map((n) => n.id)
    );
  }, [sessionPreviewData, graphData]);

  const previewRelationshipKeys = useMemo<Set<string>>(() => {
    if (!sessionPreviewData || !graphData) return new Set();
    const relationshipKey = (relationship: {
      from_node_id: string;
      to_node_id: string;
      type: string;
    }) => `${relationship.from_node_id}|${relationship.to_node_id}|${relationship.type}`;
    const baseKeys = new Set(graphData.relationships.map(relationshipKey));
    return new Set(
      sessionPreviewData.relationships
        .filter((relationship) => !baseKeys.has(relationshipKey(relationship)))
        .map(relationshipKey)
    );
  }, [sessionPreviewData, graphData]);

  const [selectedNode, setSelectedNode] = useState<NodeInstance | null>(null);
  const [hierarchyData, setHierarchyData] = useState<HierarchyNode | null>(null);
  const [detailNode, setDetailNode] = useState<NodeInstance | null>(null);
  const [focusNodeId, setFocusNodeId] = useState<string | null>(initialViewState.focusNodeId);
  const [askDeweyEntityIds, setAskDeweyEntityIds] = useState<readonly string[]>([]);
  const [hoveredSchemaNodeId, setHoveredSchemaNodeId] = useState<string | null>(null);

  // Determine the center node for edge-depth filtering: use the focused/selected
  // node, or fall back to the root Organization node.
  const edgeDepthCenter = useMemo(() => {
    if (focusNodeId) return focusNodeId;
    const orgNode = activeGraphData?.nodes.find((n) => n.type?.toLowerCase() === 'organization');
    return orgNode?.id ?? null;
  }, [focusNodeId, activeGraphData]);

  const filteredGraphData = useMemo(
    () =>
      applyGraphFilters(
        activeGraphData,
        selectedNodeTypes,
        hiddenNodeIds,
        ghostNodeIds,
        previewPinnedNodeIds,
        edgeDepthCenter,
        edgeDepth
      ),
    [
      activeGraphData,
      selectedNodeTypes,
      hiddenNodeIds,
      ghostNodeIds,
      previewPinnedNodeIds,
      edgeDepthCenter,
      edgeDepth,
    ]
  );

  const [backendAuthError, setBackendAuthError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [signOutHovered, setSignOutHovered] = useState(false);
  const focusTrackingInitializedRef = useRef(false);
  const [showInfo, setShowInfo] = useState(() => !localStorage.getItem('dewey_visited'));
  const layoutRef = useRef(layout);
  const schemaModeRef = useRef(schemaMode);

  useEffect(() => {
    relCreationRef.current = relCreation;
  }, [relCreation]);

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    schemaModeRef.current = schemaMode;
  }, [schemaMode]);

  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);

  const openInfo = () => {
    if (!showInfo) {
      trackEvent('info_overlay_opened', {
        path: window.location.pathname,
        userId: email ?? 'anonymous',
      });
    }
    setShowInfo(true);
  };
  const closeInfo = () => {
    trackEvent('info_overlay_closed', {
      path: window.location.pathname,
      userId: email ?? 'anonymous',
    });
    localStorage.setItem('dewey_visited', '1');
    setShowInfo(false);
  };

  const loadGraphData = useCallback(async (): Promise<void> => {
    const showGlobalLoading = !hasLoadedGraphOnceRef.current;
    try {
      if (showGlobalLoading) {
        setLoading(true);
      }
      if (layout === 'force' || layout === '3d') {
        const data = await graphApi.getGraph();
        // Backend returns { data: { nodes, relationships } } — unwrap data envelope
        const graphPayload = (data as unknown as { data: typeof data }).data ?? data;
        setGraphData(graphPayload);
        hasLoadedGraphOnceRef.current = true;
      } else if (layout === 'icicle') {
        const data = await graphApi.getHierarchy();
        // Hierarchy endpoint returns the node directly (no data envelope)
        setHierarchyData(data);
        hasLoadedGraphOnceRef.current = true;
      } else {
        // Content mode reuses session and curation endpoints; no graph fetch here.
      }
    } catch (err) {
      if ((err as Error).message?.includes('401')) {
        setBackendAuthError(true);
      } else {
        console.error('Failed to load graph data:', err);
      }
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  }, [layout]);

  const handleLayoutChange = (nextLayout: 'force' | 'icicle' | '3d' | 'content') => {
    if (nextLayout === layout) {
      // When Schema is active, clicking 3D should switch back to graph mode.
      if (nextLayout === '3d' && schemaMode) {
        setSchemaMode(false);
      }
      return;
    }
    trackEvent('layout_changed', {
      fromLayout: layout,
      toLayout: nextLayout,
      path: window.location.pathname,
      userId: email ?? 'anonymous',
      focusNodeId: focusNodeId ?? '',
    });
    if (schemaMode) {
      setSchemaMode(false);
    }
    setLayout(nextLayout);
  };

  const handleSchemaTabClick = () => {
    if (layout !== '3d') {
      handleLayoutChange('3d');
      setSchemaMode(true);
      return;
    }
    setSchemaMode(true);
  };

  const handleTopRefresh = () => {
    if (layout === 'content') {
      void contentPanelRef.current?.refreshSelectedIndex();
      return;
    }
    setLayoutKey((prev) => prev + 1);
    void loadGraphData();
  };

  const viewTabs = useMemo<
    Array<{ value: 'force' | 'icicle' | '3d' | 'content'; label: string; title: string }>
  >(
    () => [
      { value: 'icicle', label: 'Capability', title: 'Capability view' },
      { value: 'force', label: 'Network', title: 'Network view' },
      { value: '3d', label: '3D', title: '3D network view' },
      ...(canUseContent
        ? [{ value: 'content' as const, label: 'Content', title: 'Content analysis view' }]
        : []),
    ],
    [canUseContent]
  );
  const activeViewTabId = viewTabs.some((tab) => tab.value === layout)
    ? `view-tab-${layout}`
    : undefined;

  useEffect(() => {
    sessionStorage.setItem('dewey.nodeTypeFilter', JSON.stringify(selectedNodeTypes));
  }, [selectedNodeTypes]);

  useEffect(() => {
    sessionStorage.setItem('dewey.edgeDepth', String(edgeDepth));
  }, [edgeDepth]);

  useEffect(() => {
    localStorage.setItem('dewey.sidebarWidth', String(sidebarWidth));
  }, [sidebarWidth]);

  // Derive a friendly first name from the email address for the sign-out button
  const firstName = email
    ? email
        .split('@')[0]
        .split('.')[0]
        .replace(/^(.)/, (c) => c.toUpperCase())
    : null;

  const highlightedNodeScores = useMemo(() => {
    const scores = new Map<string, number>();
    // Entities named by the current Ask Dewey answer are highlighted as a set so
    // the answer's shape stays legible, but at a lower score than the focused
    // node. Views map score to amber intensity, so the focused node reads as
    // dominant while its supporting context stays visible.
    for (const id of askDeweyEntityIds) {
      scores.set(id, 0.45);
    }
    if (focusNodeId) {
      scores.set(focusNodeId, 1.0);
    }
    return scores;
  }, [focusNodeId, askDeweyEntityIds]);

  const handleSearchResults = (results: SearchResult[]) => {
    setSearchResults(results);
  };

  const handleSearchResultFocus = (result: SearchResult) => {
    setSelectedNode(result.node);
    setFocusNodeId(result.node.id);
  };

  /** Focus a node referenced by an Ask Dewey answer — same path as a search result. */
  const handleFocusNode = useCallback((node: NodeInstance) => {
    setSelectedNode(node);
    setFocusNodeId(node.id);
  }, []);

  const handleClearAskDewey = useCallback(() => {
    setAskDeweyEntityIds([]);
    setFocusNodeId(null);
    setSelectedNode(null);
  }, []);

  /**
   * Records the entities grounded in the current Ask Dewey answer so they can be
   * highlighted. Bails when the set is unchanged, since the child reports on
   * every resolve and a fresh array each time would otherwise re-render forever.
   */
  const handleAskDeweyEntitiesChange = useCallback((nodeIds: string[]) => {
    setAskDeweyEntityIds((prev) =>
      prev.length === nodeIds.length && prev.every((id, i) => id === nodeIds[i]) ? prev : nodeIds
    );
  }, []);

  // When a focus node is specified (via URL or direct set), open it in the detail pane.
  // Runs when focusNodeId or graphData changes so it works whether the id or the data arrives first.
  useEffect(() => {
    if (!focusNodeId || !graphData) return;
    const match = graphData.nodes.find((n) => n.id === focusNodeId) ?? null;
    if (match) setSelectedNode(match);
  }, [focusNodeId, graphData]);

  // When graphData is mutated during an active edit session (e.g. add/delete/update),
  // clear any stale sessionPreviewData so the graph shows the live graphData.
  useEffect(() => {
    if (sessionActive) {
      setSessionPreviewData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData]);

  useEffect(() => {
    if (localStorage.getItem('dewey_ai_debug') === '1') {
      console.log('[AI debug] page-load effect', {
        pageLoadTracked,
        isAuthLoading,
        isAuthenticated,
        email,
        focusNodeId,
      });
    }
    if (pageLoadTracked || isAuthLoading) return;
    pageLoadTracked = true;
    const focusFromUrl = new URLSearchParams(window.location.search).get('focus') ?? '';
    trackPageLoad({
      eventName: 'page_load',
      userId: email ?? 'anonymous',
      focusNodeId: focusNodeId ?? focusFromUrl,
      isAuthenticated,
      path: window.location.pathname,
    });
  }, [isAuthLoading, isAuthenticated, email, focusNodeId]);

  useEffect(() => {
    if (!focusTrackingInitializedRef.current) {
      focusTrackingInitializedRef.current = true;
      return;
    }
    trackEvent('focus_node_changed', {
      focusNodeId: focusNodeId ?? '',
      layout,
      path: window.location.pathname,
      userId: email ?? 'anonymous',
    });
  }, [focusNodeId, layout, email]);

  // Keep URL in sync so deep links remain shareable.
  // Guard: skip during OAuth callback — TID SDK reads ?code & ?state from the URL
  // and React runs children's effects before parents', so without this guard the
  // URL-replace would strip the auth code before TIDProvider processes it.
  useEffect(() => {
    if (window.location.pathname === '/auth/callback') return;
    const params = new URLSearchParams(window.location.search);
    if (focusNodeId) {
      params.set('focus', focusNodeId);
    } else {
      params.delete('focus');
    }
    params.set('view', schemaMode ? 'schema' : layout);
    if (contentSourceTarget?.indexName) {
      params.set('index', contentSourceTarget.indexName);
      if (contentSourceTarget.documentId) {
        params.set('doc', contentSourceTarget.documentId);
      } else {
        params.delete('doc');
      }
      if (contentSourceTarget.documentId && contentSourceTarget.chunk) {
        params.set('chunk', contentSourceTarget.chunk);
      } else {
        params.delete('chunk');
      }
    } else {
      params.delete('index');
      params.delete('doc');
      params.delete('chunk');
    }
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}?${newSearch}`;
    window.history.replaceState(null, '', newUrl);
  }, [focusNodeId, layout, schemaMode, contentSourceTarget]);

  // Load node types and relationship types — wait for auth init to complete so the
  // token is available before the requests go out.
  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) return;
    const loadNodeTypes = async (): Promise<void> => {
      try {
        const response = await typeApi.getNodeTypes();
        // Backend returns { data: { node_types: [...] } } — unwrap data envelope first
        const anyResponse = response as unknown as { data?: { node_types?: NodeType[] } };
        const types =
          anyResponse.data?.node_types ??
          response.node_types ??
          (Array.isArray(response) ? (response as unknown as NodeType[]) : []);
        setNodeTypes(types);
      } catch (err) {
        setNodeTypes([]);
        if ((err as Error).message?.includes('401')) {
          setBackendAuthError(true);
        } else {
          console.error('Failed to load node types:', err);
        }
      }
    };

    const loadRelationshipTypes = async (): Promise<void> => {
      try {
        const response = await typeApi.getRelationshipTypes();
        // Backend returns { data: { relationship_types: [...] } } — unwrap data envelope first
        const anyResponse = response as unknown as {
          data?: { relationship_types?: RelationshipType[] };
        };
        const types =
          anyResponse.data?.relationship_types ??
          response.relationship_types ??
          (Array.isArray(response) ? (response as unknown as RelationshipType[]) : []);
        setRelationshipTypes(types);
      } catch (err) {
        setRelationshipTypes([]);
        if ((err as Error).message?.includes('401')) {
          setBackendAuthError(true);
        } else {
          console.error('Failed to load relationship types:', err);
        }
      }
    };

    loadNodeTypes();
    loadRelationshipTypes();
  }, [isAuthenticated, isAuthLoading]);

  // Load graph data when layout changes — wait for auth init to complete.
  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) return;
    loadGraphData();
  }, [isAuthenticated, isAuthLoading, loadGraphData]);

  /** Start a new edit session. */
  const handleStartEditSession = (parentSessionId?: string | null): void => {
    // Capture a pre-session snapshot so local-queue preview can replay from this baseline.
    preSessionGraphRef.current = graphData;
    startSession(parentSessionId ?? reviewSelectedSession);
  };

  // Handler: reparent node (move child to new parent)
  const handleReparent = async ({
    childNode,
    oldParent,
    newParent,
  }: ReparentArgs): Promise<void> => {
    if (!sessionActive) return;
    // Find relationship type for old parent → child
    let oldRelType: string | null = null;
    if (oldParent && relationshipTypes.length) {
      for (const rel of relationshipTypes) {
        for (const pairing of relationshipToPairings(rel)) {
          if (
            getPairingSourceId(pairing) === childNode.type &&
            getPairingTargetId(pairing) === oldParent.type
          ) {
            oldRelType = rel.name;
            break;
          }
        }
        if (oldRelType) break;
      }
    }

    // Find relationship type for new parent → child
    let newRelType: string | null = null;
    if (relationshipTypes.length) {
      for (const rel of relationshipTypes) {
        for (const pairing of relationshipToPairings(rel)) {
          if (
            getPairingSourceId(pairing) === childNode.type &&
            getPairingTargetId(pairing) === newParent.type
          ) {
            newRelType = rel.name;
            break;
          }
        }
        if (newRelType) break;
      }
    }

    if (!newRelType) {
      setErrorMessage('No valid relationship type for this reparenting.');
      return;
    }

    // Shadow model: move node in hierarchy
    if (hierarchyData) {
      deleteNodeFromHierarchy(hierarchyData, childNode.id);
      const movedNode: HierarchyNode = {
        id: childNode.id,
        type: childNode.type as string,
        name: childNode.name as string,
        description: (childNode.description as string) || '',
        children: (childNode.children as HierarchyNode[] | undefined) ?? [],
      };
      addNodeToHierarchy(hierarchyData, newParent.id, movedNode);
      setHierarchyData({ ...hierarchyData });
    }

    // Shadow model: rewire relationship in graph
    if (graphData) {
      const filteredRels =
        oldRelType && oldParent
          ? graphData.relationships.filter(
              (r) => !(r.from_node_id === childNode.id && r.to_node_id === oldParent.id)
            )
          : [...graphData.relationships];
      setGraphData({
        ...graphData,
        relationships: [
          ...filteredRels,
          {
            id: `pending-${childNode.id}-${newParent.id}`,
            type: newRelType,
            from_node_id: childNode.id,
            to_node_id: newParent.id,
            created_at: new Date().toISOString(),
          },
        ],
      });
    }

    const proposals: BatchProposalItem[] = [];

    // Queue both proposals
    if (oldRelType && oldParent) {
      proposals.push({
        operation: 'remove_relationship',
        from_node_id: childNode.id,
        to_node_id: oldParent.id,
        relationship_type: oldRelType,
      });
    }
    try {
      proposals.push({
        operation: 'add_relationship',
        from_node_id: childNode.id,
        to_node_id: newParent.id,
        relationship_type: newRelType,
      });
      await proposeBatch(proposals);
    } catch (err) {
      setErrorMessage(
        'Failed to create new parent relationship: ' + ((err as Error).message || err)
      );
    }
  };

  /** Preview a pending backend session using the explicitly selected proposal indices. */
  const handlePreviewSession = (
    proposals: ProposalSubmitEntry[],
    selectedIndices: number[] | null
  ): void => {
    if (!graphData || !selectedIndices || selectedIndices.length === 0) {
      setSessionPreviewData(null);
      return;
    }
    const selectedEntries = selectedIndices
      .map((index) => proposals[index])
      .filter((entry): entry is ProposalSubmitEntry => Boolean(entry));
    if (selectedEntries.length === 0) {
      setSessionPreviewData(null);
      return;
    }
    const previewGraph = applyQueueEntriesToGraph(
      graphData,
      selectedEntries,
      selectedEntries.length - 1
    );
    let nextFocusNodeId: string | null = null;
    for (let i = selectedEntries.length - 1; i >= 0; i -= 1) {
      const entry = selectedEntries[i];
      if (entry.entity_type === 'node' && entry.node_id) {
        nextFocusNodeId = entry.node_id;
        break;
      }
      if (entry.entity_type === 'relationship') {
        if (entry.from_node_id) {
          nextFocusNodeId = entry.from_node_id;
          break;
        }
        if (entry.to_node_id) {
          nextFocusNodeId = entry.to_node_id;
          break;
        }
      }
    }
    if (nextFocusNodeId) {
      setFocusNodeId(nextFocusNodeId);
    }

    // Fallback: if no explicit step target was found, focus the last node introduced by preview.
    if (!nextFocusNodeId) {
      for (let i = selectedEntries.length - 1; i >= 0; i -= 1) {
        const entry = selectedEntries[i];
        if (entry.entity_type === 'node' && entry.action === 'add' && entry.node_id) {
          setFocusNodeId(entry.node_id);
          break;
        }
      }
    }

    if (!nextFocusNodeId) {
      const previewNodeIds = new Set(previewGraph.nodes.map((node) => node.id));
      for (const baseNode of graphData.nodes) {
        previewNodeIds.delete(baseNode.id);
      }
      const lastIntroducedId = Array.from(previewNodeIds).pop();
      if (lastIntroducedId) {
        setFocusNodeId(lastIntroducedId);
      }
    }

    if (!nextFocusNodeId) {
      const lastPreviewNode = previewGraph.nodes[previewGraph.nodes.length - 1];
      if (lastPreviewNode?.id) {
        setFocusNodeId(lastPreviewNode.id);
      }
    }
    setSessionPreviewData(previewGraph);
  };

  /** Preview the provided local queue up to (and including) queue[index]. */
  const handlePreviewLocalIndex = (
    queueEntries: readonly ProposalSubmitEntry[],
    index: number | null
  ): void => {
    if (!preSessionGraphRef.current || index === null) {
      setSessionPreviewData(null);
      return;
    }
    setSessionPreviewData(
      applyQueueEntriesToGraph(preSessionGraphRef.current, queueEntries, index)
    );
  };

  const handleReviewSelectedSessionChange = (sessionId: string | null): void => {
    setReviewSelectedSession(sessionId);
  };

  const handleViewProposalSource = (
    indexName: string,
    documentId: string,
    chunk: string | null
  ): void => {
    setContentSourceTarget({ indexName, documentId, chunk });
    setLayout('content');
  };

  const handleSidebarResizeStart = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const container = appContentRef.current;
    if (!container) return;

    const onMouseMove = (moveEvent: MouseEvent): void => {
      const bounds = container.getBoundingClientRect();
      const desired = moveEvent.clientX - bounds.left;
      const clamped = Math.max(250, Math.min(650, desired));
      setSidebarWidth(clamped);
    };

    const onMouseUp = (): void => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (!selectedNode) {
      setDetailNode(null);
      return;
    }
    const resolvedNode =
      activeGraphData?.nodes.find((node) => node.id === selectedNode.id) ?? selectedNode;
    setDetailNode(resolvedNode);
    if (resolvedNode !== selectedNode) {
      setSelectedNode(resolvedNode);
    }
  }, [activeGraphData, selectedNode]);

  // Handler: delete node
  const handleDeleteNode = async (node: NodeInstance): Promise<void> => {
    if (!sessionActive) return;
    const children = (graphData?.relationships || []).filter((rel) => rel.to_node_id === node.id);
    if (children.length > 0) {
      setErrorMessage(
        'Cannot delete node: it has child nodes. Remove or reparent its children first.'
      );
      return;
    }
    try {
      await proposeBatch([{ operation: 'delete', target_node_id: node.id }]);
      // Shadow model: remove from local graph state immediately.
      if (graphData) {
        setGraphData({
          nodes: graphData.nodes.filter((n) => n.id !== node.id),
          relationships: graphData.relationships.filter(
            (r) => r.from_node_id !== node.id && r.to_node_id !== node.id
          ),
        });
      }
      if (hierarchyData) {
        deleteNodeFromHierarchy(hierarchyData, node.id);
        setHierarchyData({ ...hierarchyData });
      }
      window.dispatchEvent(new CustomEvent('nodeDeleted', { detail: { nodeId: node.id } }));
    } catch (err) {
      setErrorMessage('Failed to delete node: ' + ((err as Error).message || err));
    }
    // Shadow model updated — no loadGraphData() needed until saveSession/cancelSession.
  };

  const cancelRelCreation = (): void => {
    const wasActive = relCreationRef.current.active;
    if (!wasActive) {
      return;
    }
    relCreationRef.current = {
      active: false,
      sourceNode: null,
      relType: null,
      relationshipName: null,
      relationshipDirection: null,
      allowedTargets: [],
      error: null,
    };
    setRelCreation({
      active: false,
      sourceNode: null,
      relType: null,
      relationshipName: null,
      relationshipDirection: null,
      allowedTargets: [],
      error: null,
    });
  };

  const handleStartRelationshipCreation = ({
    sourceNode,
    relType,
    relationshipName,
    relationshipDirection,
    candidateNodeIds,
  }: {
    sourceNode: NodeInstance;
    relType: RelationshipType;
    relationshipName: string;
    relationshipDirection: 'incoming' | 'outgoing';
    candidateNodeIds: string[];
  }): void => {
    setRelCreation({
      active: true,
      sourceNode,
      relType,
      relationshipName,
      relationshipDirection,
      allowedTargets: candidateNodeIds,
      error: null,
    });
  };

  const handleGraphNodeSelected = async (node: NodeInstance): Promise<void> => {
    lastGraphNodeClickAtRef.current = Date.now();
    const currentRelCreation = relCreationRef.current;
    console.debug('[RelDebug] node click', {
      nodeId: node.id,
      relActive: currentRelCreation.active,
      sourceId: currentRelCreation.sourceNode?.id,
      relName: currentRelCreation.relationshipName,
      allowedTargetCount: currentRelCreation.allowedTargets.length,
      sessionActive: sessionActiveRef.current,
    });
    if (
      !currentRelCreation.active ||
      !currentRelCreation.sourceNode ||
      !currentRelCreation.relationshipName ||
      !sessionActiveRef.current
    ) {
      setSelectedNode(node);
      setFocusNodeId(node.id);
      return;
    }

    if (!currentRelCreation.allowedTargets.includes(node.id)) {
      setErrorMessage(
        `Node ${node.id} is not a valid destination for ${currentRelCreation.relationshipName}.`
      );
      setRelCreation((prev) => ({
        ...prev,
        error: 'Selected node is not a valid relationship target.',
      }));
      return;
    }

    const fromNodeId =
      currentRelCreation.relationshipDirection === 'incoming'
        ? node.id
        : currentRelCreation.sourceNode.id;
    const toNodeId =
      currentRelCreation.relationshipDirection === 'incoming'
        ? currentRelCreation.sourceNode.id
        : node.id;
    const relationshipName = currentRelCreation.relationshipName;

    try {
      console.debug('[RelDebug] propose add relationship', {
        fromNodeId,
        toNodeId,
        relType: currentRelCreation.relationshipName,
      });
      await proposeBatch([
        {
          operation: 'add_relationship',
          from_node_id: fromNodeId,
          to_node_id: toNodeId,
          relationship_type: relationshipName,
        },
      ]);

      setGraphData((prev) => {
        if (!prev) return prev;
        const alreadyExists = prev.relationships.some(
          (r) =>
            r.from_node_id === fromNodeId &&
            r.to_node_id === toNodeId &&
            r.type === relationshipName
        );
        if (alreadyExists) return prev;

        const newRel: Relationship = {
          id: `pending-${fromNodeId}-${toNodeId}-${relationshipName}`,
          type: relationshipName,
          from_node_id: fromNodeId,
          to_node_id: toNodeId,
          created_at: new Date().toISOString(),
        };
        return {
          ...prev,
          relationships: [...prev.relationships, newRel],
        };
      });

      setSelectedNode(node);
      setFocusNodeId(node.id);
      console.debug('[RelDebug] relationship add success', {
        fromNodeId,
        toNodeId,
        relType: relationshipName,
      });
      cancelRelCreation();
    } catch (err) {
      console.debug('[RelDebug] relationship add failed', {
        fromNodeId,
        toNodeId,
        relType: currentRelCreation.relationshipName,
        error: (err as Error).message || err,
      });
      setErrorMessage(`Failed to create relationship: ${(err as Error).message || err}`);
      setRelCreation((prev) => ({
        ...prev,
        error: (err as Error).message || 'Failed to create relationship.',
      }));
    }
  };

  const handleGraphBackgroundClick = (): void => {
    // react-force-graph can emit a background click during node interactions;
    // ignore near-simultaneous background events so node selection/relationship
    // completion is not immediately undone.
    if (Date.now() - lastGraphNodeClickAtRef.current < 250) {
      console.debug('[RelDebug] background click ignored due to click guard');
      return;
    }
    // While selecting a relationship destination, users often pan/zoom by
    // interacting with background space. Do not cancel relationship mode.
    if (relCreationRef.current.active) {
      console.debug('[RelDebug] background click ignored while relationship mode active');
      return;
    }
    if (canMutate) {
      setSelectedNode(null);
    }
  };

  const handleAddNode = async (
    nodeData: Pick<NodeInstance, 'id' | 'name' | 'description' | 'type'> & {
      properties?: Record<string, string>;
    }
  ): Promise<void> => {
    if (!sessionActive) return;

    const idExistsInGraph =
      graphData &&
      Array.isArray(graphData.nodes) &&
      graphData.nodes.some((n) => n.id === nodeData.id);
    const idExistsInHierarchy = hierarchyData && findNodeInHierarchy(hierarchyData, nodeData.id);
    if (idExistsInGraph || idExistsInHierarchy) {
      throw new Error('A node with this ID already exists.');
    }

    const nodeProperties = Object.fromEntries(
      Object.entries(nodeData.properties ?? {}).filter(([, value]) => (value || '').trim() !== '')
    );
    const selectedType = nodeTypes.find((typeDef) => typeDef.type === nodeData.type);
    const isParticularType =
      selectedType?.labels?.some((label) => label.toLowerCase() === 'particular') ?? false;

    await proposeBatch([
      {
        operation: 'add',
        node_type: nodeData.type,
        node: {
          id: nodeData.id,
          name: nodeData.name,
          description: nodeData.description,
          properties: Object.keys(nodeProperties).length > 0 ? nodeProperties : undefined,
          aliases: Array.isArray(nodeProperties.aliases)
            ? nodeProperties.aliases.filter((alias): alias is string => typeof alias === 'string')
            : undefined,
        },
      },
    ]);

    const newNodeInstance: NodeInstance = {
      id: nodeData.id,
      type: nodeData.type,
      name: nodeData.name,
      description: nodeData.description || '',
      aliases: extractAliases(nodeProperties),
      properties: Object.keys(nodeProperties).length > 0 ? nodeProperties : undefined,
      particular: buildParticularMetadata(
        nodeProperties,
        isParticularType || Object.keys(nodeProperties).length > 0
      ),
    };

    if (graphData) {
      setGraphData({
        ...graphData,
        nodes: [...graphData.nodes, newNodeInstance],
      });
    }

    setSelectedNode(newNodeInstance);
    setFocusNodeId(newNodeInstance.id);
  };

  const handleRestoreFullGraph = (): void => {
    setHiddenNodeIds(new Set());
    setGhostNodeIds(new Set());
  };

  const handleOpenLinkContextMenu = (
    link: { id: string; type: string; sourceId: string; targetId: string },
    position: { x: number; y: number }
  ): void => {
    setLinkMenuState({ link, position });
  };

  /**
   * Hide one side of a link transitively.  The directly-hidden node becomes
   * a "ghost" (invisible but still in the graph so the edge is clickable).
   * All other nodes reachable only from the hidden side are fully removed.
   */
  const handleHideLinkSide = (linkId: string, nodeIdToHide: string, nodeIdToKeep: string): void => {
    if (!graphData) return;

    // 1. Find all nodes reachable from the kept side (ignoring the clicked edge).
    const reachableFromKept = new Set<string>();
    const queue: string[] = [nodeIdToKeep];
    reachableFromKept.add(nodeIdToKeep);
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const rel of graphData.relationships) {
        if (rel.id === linkId) continue;
        let neighbor: string | null = null;
        if (rel.from_node_id === cur) neighbor = rel.to_node_id;
        else if (rel.to_node_id === cur) neighbor = rel.from_node_id;
        if (neighbor && !reachableFromKept.has(neighbor)) {
          reachableFromKept.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    // 2. Collect all nodes reachable from the hidden side.
    const reachableFromHidden = new Set<string>();
    const queue2: string[] = [nodeIdToHide];
    reachableFromHidden.add(nodeIdToHide);
    while (queue2.length > 0) {
      const cur = queue2.shift()!;
      for (const rel of graphData.relationships) {
        if (rel.id === linkId) continue;
        let neighbor: string | null = null;
        if (rel.from_node_id === cur) neighbor = rel.to_node_id;
        else if (rel.to_node_id === cur) neighbor = rel.from_node_id;
        if (neighbor && !reachableFromHidden.has(neighbor)) {
          reachableFromHidden.add(neighbor);
          queue2.push(neighbor);
        }
      }
    }

    // 3. Nodes to fully hide = reachable-from-hidden minus reachable-from-kept.
    const toHide = new Set<string>();
    for (const id of reachableFromHidden) {
      if (!reachableFromKept.has(id)) toHide.add(id);
    }

    // The directly-hidden node becomes a ghost (stays in graph, rendered invisible).
    setGhostNodeIds((prev) => {
      const next = new Set(prev);
      next.add(nodeIdToHide);
      return next;
    });

    // All nodes in toHide (including the direct node) go into hiddenNodeIds.
    // Ghost nodes are kept by applyGraphFilters; the rest are removed.
    setHiddenNodeIds((prev) => {
      const next = new Set(prev);
      for (const id of toHide) next.add(id);
      return next;
    });
  };

  /** Show (un-hide) a ghost node and all nodes that were transitively hidden with it. */
  const handleShowLinkSide = (nodeId: string): void => {
    // Remove from ghost set.
    setGhostNodeIds((prev) => {
      const next = new Set(prev);
      next.delete(nodeId);
      return next;
    });
    // Un-hide the ghost node and any transitive nodes that are now reachable
    // from non-hidden nodes.  Simplest correct approach: remove the ghost node
    // from hidden and then do a reachability pass to un-hide anything that is
    // only hidden because of this ghost.
    setHiddenNodeIds((prev) => {
      const next = new Set(prev);
      next.delete(nodeId);
      // Also un-hide transitive nodes that were pulled in when this node was hidden.
      // Walk from nodeId through edges; any node that is currently hidden AND
      // reachable from nodeId (without passing through another ghost) gets un-hidden.
      if (graphData) {
        const toRestore = new Set<string>();
        const q: string[] = [nodeId];
        toRestore.add(nodeId);
        while (q.length > 0) {
          const cur = q.shift()!;
          for (const rel of graphData.relationships) {
            let neighbor: string | null = null;
            if (rel.from_node_id === cur) neighbor = rel.to_node_id;
            else if (rel.to_node_id === cur) neighbor = rel.from_node_id;
            if (neighbor && !toRestore.has(neighbor) && next.has(neighbor)) {
              toRestore.add(neighbor);
              q.push(neighbor);
            }
          }
        }
        for (const id of toRestore) next.delete(id);
      }
      return next;
    });
  };

  /** Resolve a node ID to its display name. */
  const resolveNodeName = (id: string): string => {
    const node = graphData?.nodes.find((n) => n.id === id);
    return node?.name || id;
  };

  const handleSaveNode = async (
    nodeData: Pick<NodeInstance, 'id'> & Partial<NodeInstance>
  ): Promise<void> => {
    if (!sessionActive) return;
    // Collect all editable scalar fields that differ from the current node and submit
    // one step entry per changed field.
    const immutable = new Set([
      'id',
      'type',
      'is_live',
      'created_at',
      'updated_at',
      'proposed_session_id',
      'proposed_action',
      'proposed_time',
      'proposed_author',
      'proposed_justification',
      'proposed_property_name',
      'proposed_property_value',
      'proposal',
    ]);
    const fields: Array<[string, string]> = [];
    for (const [key, value] of Object.entries(nodeData)) {
      if (immutable.has(key)) {
        continue;
      }
      if (key === 'properties' && value && typeof value === 'object') {
        for (const [propertyName, propertyValue] of Object.entries(
          value as Record<string, unknown>
        )) {
          fields.push([propertyName, String(propertyValue ?? '')]);
        }
        continue;
      }
      fields.push([key, String(value ?? '')]);
    }
    if (fields.length === 0) return;
    try {
      await proposeBatch([
        {
          operation: 'update',
          target_node_id: nodeData.id,
          field_updates: fields.map(([fieldName, fieldValue]) => ({
            field_name: fieldName,
            field_value: String(fieldValue ?? ''),
          })),
        },
      ]);
      // Shadow model: apply field changes locally and mark as pending.
      if (graphData) {
        setGraphData({
          ...graphData,
          nodes: graphData.nodes.map((n) => {
            if (n.id !== nodeData.id) return n;
            const pendingNames = [...(n.pending_property_names || [])];
            const pendingValues = [...(n.pending_property_values || [])];
            let updated: NodeInstance = { ...n };
            for (const [fName, fValue] of fields) {
              updated = applyNodeFieldUpdate(updated, fName, fValue);
              if (!pendingNames.includes(fName)) {
                pendingNames.push(fName);
                pendingValues.push(String(fValue ?? ''));
              }
            }
            return {
              ...updated,
              pending_property_names: pendingNames,
              pending_property_values: pendingValues,
            };
          }),
        });
      }
      if (hierarchyData) {
        for (const [fName, fValue] of fields) {
          updateNodeInHierarchy(hierarchyData, nodeData.id, fName, String(fValue ?? ''));
        }
        setHierarchyData({ ...hierarchyData });
      }
    } catch (err) {
      setErrorMessage('Failed to save node: ' + ((err as Error).message || err));
    }
  };

  const validateSessionCapabilityLinks = (): { valid: boolean; message?: string } => {
    if (!graphData) {
      return { valid: true };
    }

    const coreTaxonomyTypes = new Set([
      'organization',
      'sector',
      'domain',
      'subdomain',
      'capability',
      'function',
    ]);
    const taxonomyTypeSet = new Set(
      nodeTypes
        .filter(
          (nt) =>
            (nt.labels || []).some((label) => label.toLowerCase() === 'taxonomy') ||
            coreTaxonomyTypes.has((nt.type || '').toLowerCase())
        )
        .map((nt) => (nt.type || '').toLowerCase())
    );

    const netAddedNodeIds = new Set<string>();
    for (const entry of queue) {
      if (entry.entity_type !== 'node' || !entry.node_id) continue;
      if (entry.action === 'add') netAddedNodeIds.add(entry.node_id);
      if (entry.action === 'delete') netAddedNodeIds.delete(entry.node_id);
    }

    if (netAddedNodeIds.size === 0) {
      return { valid: true };
    }

    if (taxonomyTypeSet.size === 0) {
      return { valid: true };
    }

    const nodesById = new Map(graphData.nodes.map((n) => [n.id, n]));
    const adjacency = new Map<string, Set<string>>();
    const addEdge = (a: string, b: string): void => {
      if (!adjacency.has(a)) adjacency.set(a, new Set());
      adjacency.get(a)?.add(b);
    };
    for (const rel of graphData.relationships) {
      addEdge(rel.from_node_id, rel.to_node_id);
      addEdge(rel.to_node_id, rel.from_node_id);
    }

    const reachesTaxonomy = (startId: string): boolean => {
      const start = nodesById.get(startId);
      if (!start) return false;
      if (taxonomyTypeSet.has((start.type || '').toLowerCase())) return true;

      const visited = new Set<string>([startId]);
      const queueIds: string[] = [startId];

      while (queueIds.length > 0) {
        const current = queueIds.shift() as string;
        const neighbors = adjacency.get(current);
        if (!neighbors) continue;
        for (const neighbor of neighbors) {
          if (visited.has(neighbor)) continue;
          visited.add(neighbor);
          const neighborNode = nodesById.get(neighbor);
          if (neighborNode && taxonomyTypeSet.has((neighborNode.type || '').toLowerCase())) {
            return true;
          }
          queueIds.push(neighbor);
        }
      }

      return false;
    };

    const failing: string[] = [];

    for (const nodeId of netAddedNodeIds) {
      const candidate = nodesById.get(nodeId);
      if (!candidate) continue;
      if (!reachesTaxonomy(nodeId)) {
        failing.push(nodeId);
      }
    }

    if (failing.length > 0) {
      return {
        valid: false,
        message: `Cannot submit session. Proposed nodes must relate transitively to a taxonomy-labeled node: ${failing.join(', ')}`,
      };
    }

    return { valid: true };
  };

  // TID still resolving auth state — show nothing briefly to avoid flash,
  // but recover with a sign-in prompt if stuck for too long.
  if (isAuthLoading) {
    if (!authStuck) return null;
    const handleRecovery = () => {
      const clientId = process.env.REACT_APP_TID_CLIENT_ID;
      if (clientId) {
        const storageKey = `tid-session-${clientId}`;
        localStorage.removeItem(`${storageKey}-token`);
        localStorage.removeItem(`${storageKey}-user`);
      }
      login();
    };
    return (
      <div className="layout">
        <header className="App-header">
          <div className="app-brand">
            <img src="/dewey_logo.svg" alt="Dewey logo" className="app-brand__logo" />
            <span className="app-brand__name">Trimble Dewey</span>
          </div>
          <div className="header-auth">
            <button className="btn-auth" onClick={handleRecovery}>
              Sign in
            </button>
          </div>
        </header>
        <div className="App-content">
          <main className="main-content">
            <div
              className="graph-view"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="loading">Session expired — please sign in again</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Backend rejected our token — show sign-in pane so the user can re-authenticate
  if (backendAuthError) {
    return (
      <div className="layout">
        {showInfo && <InfoOverlay onClose={closeInfo} />}
        <header className="App-header">
          <div className="app-brand" onClick={openInfo} style={{ cursor: 'pointer' }}>
            <img src="/dewey_logo.svg" alt="Dewey logo" className="app-brand__logo" />
            <span className="app-brand__name">Trimble Dewey</span>
          </div>
          <div className="header-auth">
            <button
              className="btn-auth"
              onClick={() => {
                setBackendAuthError(false);
                login();
              }}
            >
              Sign in
            </button>
          </div>
        </header>
        <div className="App-content">
          <main className="main-content">
            <div
              className="graph-view"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="loading">Not authorized to view data</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Not authenticated — prompt sign-in instead of loading data
  if (!isAuthenticated) {
    return (
      <div className="layout">
        {showInfo && <InfoOverlay onClose={closeInfo} />}
        <header className="App-header">
          <div className="app-brand" onClick={openInfo} style={{ cursor: 'pointer' }}>
            <img src="/dewey_logo.svg" alt="Dewey logo" className="app-brand__logo" />
            <span className="app-brand__name">Trimble Dewey</span>
          </div>
          <div className="header-auth">
            <button className="btn-auth" onClick={login}>
              Sign in
            </button>
          </div>
        </header>
        <div className="App-content">
          <main className="main-content">
            <div
              className="graph-view"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div className="loading">Sign in to TID to view data</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="graph-view">
        <div className="loading">Loading graph...</div>
      </div>
    );
  }

  return (
    <div className="layout">
      {showInfo && <InfoOverlay onClose={closeInfo} />}
      <header className="App-header">
        <div className="app-brand" onClick={openInfo} style={{ cursor: 'pointer' }}>
          <img src="/dewey_logo.svg" alt="Dewey logo" className="app-brand__logo" />
          <span className="app-brand__name">Trimble Dewey</span>
        </div>
        <div className="header-auth">
          <LocalDemoSwitcher />
          {isAuthenticated && permissions.filter(permission => permission !== 'viewer').map(permission => (
            <span key={permission} className={`auth-role auth-role--${permission}`}>{permission}</span>
          ))}
          {isAuthenticated && permissions.filter(permission => permission !== 'viewer').length === 0 && (
            <span className="auth-role auth-role--viewer">viewer</span>
          )}
          {isAuthenticated ? (
            <button
              className="btn-auth"
              onClick={logout}
              onMouseEnter={() => setSignOutHovered(true)}
              onMouseLeave={() => setSignOutHovered(false)}
              title="Sign out"
            >
              <span style={{ display: 'inline-grid' }}>
                <span
                  style={{ gridArea: '1/1', visibility: signOutHovered ? 'hidden' : 'visible' }}
                >
                  {firstName ?? 'Sign out'}
                </span>
                <span
                  style={{ gridArea: '1/1', visibility: signOutHovered ? 'visible' : 'hidden' }}
                >
                  Sign out
                </span>
              </span>
            </button>
          ) : (
            <button className="btn-auth" onClick={login}>
              Sign in
            </button>
          )}
        </div>
      </header>
      <div className="App-content" ref={appContentRef}>
        <div className="pane-view-controls" style={{ left: sidebarWidth + 22 }}>
          <div className="pane-view-tabs" role="tablist" aria-label="View selector">
            <button
              className={`pane-view-tab${layout === '3d' && schemaMode ? ' pane-view-tab--active' : ''}`}
              onClick={handleSchemaTabClick}
              title="Schema"
              aria-label="Schema"
              role="tab"
              aria-selected={layout === '3d' && schemaMode}
            >
              Schema
            </button>
            {viewTabs.map((tab) => {
              const isActive = layout === tab.value && !(tab.value === '3d' && schemaMode);
              return (
                <button
                  key={tab.value}
                  className={`pane-view-tab${isActive ? ' pane-view-tab--active' : ''}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="main-view-panel"
                  id={`view-tab-${tab.value}`}
                  onClick={() => handleLayoutChange(tab.value)}
                  title={tab.title}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <button
            className="pane-refresh-btn"
            onClick={handleTopRefresh}
            title={layout === 'content' ? 'Refresh selected index documents' : 'Refresh graph data'}
            aria-label={
              layout === 'content' ? 'Refresh selected index documents' : 'Refresh graph data'
            }
          >
            <i className="modus-icons" aria-hidden="true">
              refresh
            </i>
          </button>
        </div>
        <aside className="sidebar" style={{ width: sidebarWidth }}>
          <ControlsPanel
            layout={layout}
            onSearchResults={handleSearchResults}
            includePendingSearch={sessionActive}
            searchResultCount={searchResults.length}
            searchResults={searchResults}
            onFocusSearchResult={handleSearchResultFocus}
            focusNodeId={focusNodeId}
            graphNodes={activeGraphData?.nodes}
            onFocusNode={handleFocusNode}
            onAskDeweyEntitiesChange={handleAskDeweyEntitiesChange}
            onClearAskDewey={handleClearAskDewey}
            nodeTypes={nodeTypes}
            hiddenActive={hiddenNodeIds.size > 0 || ghostNodeIds.size > 0}
            onRestoreHidden={handleRestoreFullGraph}
            schemaMode={schemaMode}
            onSchemaModeChange={setSchemaMode}
            errorMessage={errorMessage}
            onDismissError={() => setErrorMessage(null)}
            relCreation={relCreation}
            onCancelRelCreation={cancelRelCreation}
            onSaveSession={() => {
              const validation = validateSessionCapabilityLinks();
              if (!validation.valid) {
                setErrorMessage(validation.message ?? 'Session validation failed.');
                return;
              }
              setSessionPreviewData(null);
              saveSession(loadGraphData);
            }}
            onCancelSession={() => {
              setSessionPreviewData(null);
              cancelSession();
              // Restore the pre-edit graph snapshot instead of re-fetching.
              if (preSessionGraphRef.current) {
                setGraphData(preSessionGraphRef.current);
                preSessionGraphRef.current = null;
              }
            }}
            onProposalActioned={loadGraphData}
            canApprove={hasPermission('curator')}
            localQueue={queue as ProposalSubmitEntry[]}
            onPreviewSession={handlePreviewSession}
            onPreviewLocalIndex={handlePreviewLocalIndex}
            onViewSource={handleViewProposalSource}
            onSelectedSessionChange={handleReviewSelectedSessionChange}
            requestedSessionId={reviewSelectedSession}
            onResume={(sid) => {
              preSessionGraphRef.current = graphData;
              resumeSession(sid);
              setLayout('content');
            }}
            onCancelSessionById={async (sid) => {
              await proposalApi.cancelSession(sid);
              setSessionPreviewData(null);
            }}
            onModify={(parentSid) => handleStartEditSession(parentSid)}
            onApprove={(sid) => {
              proposalApi
                .approveSession(sid)
                .then(loadGraphData)
                .catch((err: unknown) =>
                  setErrorMessage('Approve failed: ' + ((err as Error).message || err))
                );
            }}
            activeSessionId={currentSessionId || undefined}
            isAdmin={isAdmin}
            isCurator={hasPermission('curator')}
            permissions={permissions}
            onSchemaRulesUpdated={loadGraphData}
            sessionRefreshKey={sessionRefreshKey}
          >
            <NodeDetail
              node={detailNode}
              graphNodes={filteredGraphData?.nodes ?? []}
              relationshipRules={relationshipTypes}
              nodeTypes={nodeTypes}
              onEditNode={handleSaveNode}
              onSummarizeNode={async (node) => {
                const session = await proposalApi.createSession({
                  title: `Summary backfill - ${node.name || node.id}`,
                });
                await curationApi.summarizeNode(session.sessionId, { node_id: node.id });
                setSessionRefreshKey((prev) => prev + 1);
              }}
              onAddNode={handleAddNode}
              onStartRelationshipCreation={handleStartRelationshipCreation}
              onCancelRelationshipCreation={cancelRelCreation}
              canEdit={canMutate}
              onDeleteNode={handleDeleteNode}
            />
          </ControlsPanel>
          {isAuthenticated ? <AdminPanel isAdmin={isAdmin} /> : null}
        </aside>
        <div
          className="sidebar-resize-handle"
          onMouseDown={handleSidebarResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize tools panel"
          title="Drag to resize tools panel"
        />
        <main
          className="main-content"
          id="main-view-panel"
          role="tabpanel"
          aria-labelledby={activeViewTabId}
        >
          <div className="graph-view" style={{ position: 'relative' }}>
            {(layout === 'force' || layout === '3d') && !schemaMode && (
              <TypeFilterLegend
                nodeTypes={nodeTypes}
                selectedNodeTypes={selectedNodeTypes}
                onNodeTypeFilterChange={setSelectedNodeTypes}
              />
            )}

            {layout === 'content' ? (
              <ContentPanel
                ref={contentPanelRef}
                currentUserSubject={sub}
                onContentTargetChange={handleContentTargetChange}
                initialIndex={contentSourceTarget?.indexName}
                initialDocumentId={contentSourceTarget?.documentId}
                initialChunk={contentSourceTarget?.chunk}
                onSelectSession={(sid) => setReviewSelectedSession(sid)}
                onSessionSubmitted={() => {
                  loadGraphData();
                  setSessionRefreshKey((prev) => prev + 1);
                }}
              />
            ) : (
              <GraphErrorBoundary
                onRetry={() => {
                  setLayoutKey((prev) => prev + 1);
                  void loadGraphData();
                }}
              >
                {layout === 'force' ? (
                  <ForceDirectedView
                    graphData={filteredGraphData}
                    onNodeClick={handleGraphNodeSelected}
                    onBackgroundClick={handleGraphBackgroundClick}
                    relCreation={relCreation}
                    highlightedNodeScores={highlightedNodeScores}
                    previewNodeIds={previewPinnedNodeIds}
                    previewRelationshipKeys={previewRelationshipKeys}
                    layoutKey={layoutKey}
                  />
                ) : layout === '3d' ? (
                  <ForceGraph3DView
                    graphData={schemaMode ? schemaGraphData : filteredGraphData}
                    renderMode={schemaMode ? 'network' : 'tree'}
                    onNodeClick={handleGraphNodeSelected}
                    onBackgroundClick={handleGraphBackgroundClick}
                    setFocusNodeId={relCreation.active ? undefined : setFocusNodeId}
                    focusNodeId={focusNodeId}
                    highlightedNodeScores={highlightedNodeScores}
                    relationshipCandidateNodeIds={
                      relCreation.active ? relCreation.allowedTargets : []
                    }
                    onLinkContextMenu={schemaMode ? undefined : handleOpenLinkContextMenu}
                    ghostNodeIds={ghostNodeIds}
                    previewNodeIds={previewPinnedNodeIds}
                    previewRelationshipKeys={previewRelationshipKeys}
                    layoutKey={layoutKey}
                    schemaMode={schemaMode}
                    hoveredSchemaNodeId={hoveredSchemaNodeId}
                    onHoveredSchemaNodeChange={setHoveredSchemaNodeId}
                    schemaBfoAssignments={schemaBfoAssignments}
                  />
                ) : (
                  <IcicleView
                    graphData={hierarchyData}
                    nodeTypes={nodeTypes}
                    relationshipTypes={relationshipTypes}
                    setSelectedNode={(node) => setSelectedNode(node as unknown as NodeInstance)}
                    reparent={handleReparent}
                    focusNodeId={focusNodeId}
                    setFocusNodeId={setFocusNodeId}
                    canEdit={canMutate}
                    highlightedNodeScores={highlightedNodeScores}
                  />
                )}
              </GraphErrorBoundary>
            )}

            {linkMenuState && (
              <LinkContextMenu
                position={linkMenuState.position}
                link={linkMenuState.link}
                nodeName={resolveNodeName}
                ghostNodeIds={ghostNodeIds}
                onHideSide={handleHideLinkSide}
                onShowSide={handleShowLinkSide}
                onClose={() => setLinkMenuState(null)}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
